import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, ChevronDown, LifeBuoy } from "lucide-react";
import { redirectToLogin } from "../../utils/auth";
import { useUser } from "../../context/UserContext";

// Route → readable page title, shown in the header now that navigation lives in the sidebar.
const PAGE_TITLES = [
  { prefix: "/Dashboard", title: "Support Tickets" },
  { prefix: "/Form", title: "New Ticket" },
  { prefix: "/Ticket", title: "Ticket Details" },
];

const getPageTitle = (pathname) =>
  PAGE_TITLES.find(({ prefix }) => pathname.startsWith(prefix))?.title ?? "";

// Falls back to a formatted email local-part when the user has no display name
// (e.g. partners, or a session hydrated before `name` was cached).
const displayName = (name, email) => {
  if (name) return name;
  const local = (email || "").split("@")[0];
  return local
    .split(".")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const initials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useUser();
  const { username, name } = user;
  const navigate = useNavigate();
  const location = useLocation();
  const cardRef = useRef(null);
  const buttonRef = useRef(null);
  const [escOpen, setEscOpen] = useState(false);
  const [escRows, setEscRows] = useState([]);
  const authToken = localStorage.getItem("authToken");

  const openEscalation = () => {
    setEscOpen(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/API/V2/config/escalation-matrix`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setEscRows(Array.isArray(data) ? data : []))
      .catch(() => setEscRows([]));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const resolvedName = displayName(name, username);
  const pageTitle = getPageTitle(location.pathname);

  return (
    <>
    <div className="flex items-center h-[72px] w-full bg-gradient-to-r from-jiojbm60 to-jiojbm40 py-3 px-7 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-20 relative">
      <div
        className="flex items-center gap-x-3 cursor-pointer flex-shrink-0"
        onClick={() => navigate("/Dashboard")}
      >
        <span className="w-10 h-10 rounded-xl bg-white grid place-items-center flex-shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3C7 3 3 6.4 3 10.6c0 2.3 1.2 4.3 3.2 5.7-.1 1-.5 2.3-1.4 3.3 1.7-.2 3-.9 3.9-1.6.9.2 2 .4 3.3.4 5 0 9-3.4 9-7.8S17 3 12 3Z"
              fill="#059669"
            />
          </svg>
        </span>
        <div className="text-white font-bold text-lg leading-tight">Sandesh NNI Support</div>
      </div>

      {pageTitle && (
        <>
          <div className="h-6 w-px bg-white/25 mx-5 flex-shrink-0" />
          <div className="text-white/90 font-semibold text-sm truncate">{pageTitle}</div>
        </>
      )}

      <div className="ml-auto flex items-center gap-6 flex-shrink-0">
        <button
          type="button"
          onClick={openEscalation}
          className="flex items-center gap-2 text-white/90 text-sm font-semibold hover:text-white transition-colors"
        >
          <LifeBuoy size={16} />
          Escalation Matrix
        </button>
        <div ref={buttonRef} className="relative">
          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-white/10 transition-colors"
          >
            <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center font-bold text-jiojbm50 text-sm flex-shrink-0">
              {initials(resolvedName)}
            </div>
            <span className="font-semibold text-white text-sm max-w-[160px] truncate">
              {resolvedName}
            </span>
            <ChevronDown
              size={16}
              className={`text-white/80 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isOpen && (
            <div
              ref={cardRef}
              className="absolute overflow-hidden flex flex-col z-30 top-12 right-0 w-56 shadow-[0_4px_16px_rgba(0,0,0,0.15)] bg-white rounded-xl border border-gray-100"
            >
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="font-bold text-sm text-gray-800 truncate">{resolvedName}</div>
                <div className="text-xs text-gray-500 truncate">{username}</div>
              </div>

              <button
                type="button"
                className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-jiojbm20 hover:text-jiojbm50 text-left"
                onClick={() => redirectToLogin()}
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>

    {escOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
        onClick={() => setEscOpen(false)}
      >
        <div
          className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-bold text-gray-800">Escalation Matrix</h2>
            <button
              type="button"
              onClick={() => setEscOpen(false)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
            >
              ✕
            </button>
          </div>
          <div className="p-6">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs uppercase text-gray-500">
                  <th className="p-3 border-b border-gray-200">Level</th>
                  <th className="p-3 border-b border-gray-200">Contact</th>
                  <th className="p-3 border-b border-gray-200">Reach Via</th>
                </tr>
              </thead>
              <tbody>
                {escRows.map((row) => (
                  <tr key={row.level} className="border-t">
                    <td className="p-3 font-semibold text-jiojbm50">{row.level}</td>
                    <td className="p-3">{row.contact}</td>
                    <td className="p-3">
                      {row.phone && <div>{row.phone}</div>}
                      {row.email && <div className="text-gray-500">{row.email}</div>}
                    </td>
                  </tr>
                ))}
                {escRows.length === 0 && (
                  <tr>
                    <td colSpan={3} className="p-4 text-center text-gray-400">
                      No escalation contacts configured.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )}
    </>
  );
};
